// @ts-ignore
import traverseISODate from '@segment/isodate-traverse';

import { type Context, type EventOverrides } from '../types';

import { type SegmentContextDef } from './types';

export const buildContext = (context: Context, overrides?: EventOverrides): SegmentContextDef => {
	const result = {
		...context.context,
		userAgent: navigator.userAgent,
	};

	// use nestedRemoveUndefinedValues to strip undefined properties before
	// spreading the override object since we want properties that explicitly
	// set to 'undefined' to not overwrite any values, just like properties
	// that are 'undefined' because they're not set at all will.
	if (overrides?.excludedProperties) {
		result.excludedProperties = {
			...result.excludedProperties,
			...nestedRemoveUndefinedValues(overrides.excludedProperties, '$.excludedProperties'),
		};
	}
	if (overrides?.userConsents) {
		result.userConsents = {
			...result.userConsents,
			...nestedRemoveUndefinedValues(overrides.userConsents, '$.userConsents'),
		};
	}

	// Remove any falsey properties since we only want to have exclusions
	// that were explicitly declared as excluded.
	if (result.excludedProperties) {
		result.excludedProperties = removeFalseyProperties(result.excludedProperties);
	}

	return result;
};

export const prepareEventContext = <T extends object>(context: T): T => {
	const propertiesWithoutUndefined = nestedRemoveUndefinedValues(context, '$.context');
	return traverseISODate(propertiesWithoutUndefined);
};

export const prepareEventProperties = <T extends object>(properties: T): T => {
	const propertiesWithoutUndefined = nestedRemoveUndefinedValues(properties, '$.properties');

	// Remove any override properties that shouldn't be included in the event body
	delete (propertiesWithoutUndefined as EventOverrides).excludedProperties;
	delete (propertiesWithoutUndefined as EventOverrides).userConsents;

	return traverseISODate(propertiesWithoutUndefined);
};

// Shallow removal of all falsey properties from an object, returning a new object.
const removeFalseyProperties = <T extends object>(obj: T): { [K in keyof T]: T[K] } => {
	const result = {} as any;

	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			// If the value is not strictly true, map it to undefined
			if (obj[key]) {
				result[key] = obj[key];
			}
		}
	}

	return result as { [K in keyof T]: T[K] };
};

// This function will essentially deepClone as well
const nestedRemoveUndefinedValues = <T extends object>(obj: T, path: string): T => {
	const recursionDepthLimit = 512;

	const nestedRemoveUndefinedValuesImpl = (
		source: any,
		processedReferences: Map<object, string>,
		path: string,
		depth: number,
	) => {
		if (source === null || typeof source !== 'object') {
			return source;
		}

		// If the level of nesting is egregiously excessive we'll stop processing any further to
		// avoid crashing due to stack exhaustion, instead we return a note object.
		if (depth > recursionDepthLimit) {
			return {
				_depthLimitExceeded: true,
				message: `Object nesting depth limit exceeded at path '${path}' with depth '${depth}'`,
			};
		}

		// If this object has already been seen then processing it would create a circular reference,
		// instead we return a note object.
		if (processedReferences.has(source)) {
			const originalPath = processedReferences.get(source);
			return {
				_circularReference: true,
				message: `Circular reference to object at original path '${originalPath}' detected at path '${path}'`,
				originalPath: originalPath,
			};
		}
		processedReferences.set(source, path);

		const clone: any = {};
		Object.keys(source).forEach((key) => {
			const value = source[key];
			if (value !== undefined) {
				if (Array.isArray(value)) {
					const newArray: any[] = [];
					value.forEach((v, i) => {
						const newValue = nestedRemoveUndefinedValuesImpl(
							v,
							processedReferences,
							`${path}.${key}[${i}]`,
							depth + 1,
						);
						if (newValue !== undefined) {
							newArray.push(newValue);
						}
					});
					clone[key] = newArray;
				} else {
					const newValue = nestedRemoveUndefinedValuesImpl(
						value,
						processedReferences,
						`${path}.${key}`,
						depth + 1,
					);
					if (newValue !== undefined) {
						clone[key] = newValue;
					}
				}
			}
		});

		processedReferences.delete(source);
		return clone;
	};

	return nestedRemoveUndefinedValuesImpl(obj, new Map<object, string>(), path, 0);
};
